import { createSlice } from '@reduxjs/toolkit';
import { PrivilegesObj, User } from './usersListModel';
import { UserGroup } from '../userGroups/userGroupModel';
import { MyClient } from '../myClients/myClientsModel';
import {
  activateUserThunk,
  deactivateUserThunk,
  deleteUserThunk,
  editUserThunk,
  getActiveUsersThunk,
  getAllGroupsThunk,
  getClientDetailsThunk,
  getDraftUsersThunk,
  getGroupDetailsThunk,
  getUserDetailsThunk,
  saveUserThunk,
} from './userListActions';
import { getPrivilegesListForGroups } from './userListHelper';
import cloneDeep from 'lodash/cloneDeep';

export interface UsersData {
  loading: boolean;
  activeUsersList: User[];
  activeUsersListLoading: boolean;
  draftUsersList: User[];
  groups: UserGroup[];
  clients: MyClient[];
  privilegesList: PrivilegesObj;
  privilegesLoading: boolean;
  selectedUserDetails: User;
}

const initialState: UsersData = {
  loading: false,
  activeUsersList: [],
  activeUsersListLoading: true,
  draftUsersList: [],
  groups: [],
  clients: [],
  privilegesList: null,
  privilegesLoading: false,
  selectedUserDetails: null,
};

const usersSlice = createSlice({
  name: 'Users',
  initialState,
  reducers: {
    clearPrivilegesList: (state) => {
      state.privilegesList = null;
    },
    setModifiedPrivileges: (state, action) => {
      state.privilegesList = action.payload;
    },
    clearUserDetails: (state) => {
      state.activeUsersListLoading = true;
      state.selectedUserDetails = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActiveUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getActiveUsersThunk.fulfilled, (state, action) => {
        state.activeUsersList = action.payload;
        state.loading = false;
      })
      .addCase(getActiveUsersThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getUserDetailsThunk.pending, (state) => {
        state.loading = true;
        state.selectedUserDetails = null;
      })
      .addCase(getUserDetailsThunk.fulfilled, (state, action) => {
        state.selectedUserDetails = action.payload;
        state.loading = false;
      })
      .addCase(getUserDetailsThunk.rejected, (state) => {
        state.loading = false;
        state.selectedUserDetails = null;
      })
      .addCase(getAllGroupsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllGroupsThunk.fulfilled, (state, action) => {
        state.groups = action.payload || [];
        state.loading = false;
      })
      .addCase(getAllGroupsThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getGroupDetailsThunk.pending, (state) => {
        state.privilegesLoading = true;
      })
      .addCase(getGroupDetailsThunk.fulfilled, (state, action) => {
        state.privilegesList = getPrivilegesListForGroups(cloneDeep(action.payload) || []);
        state.privilegesLoading = false;
      })
      .addCase(getGroupDetailsThunk.rejected, (state) => {
        state.privilegesLoading = false;
      })
      .addCase(getClientDetailsThunk.pending, (state) => {
        state.privilegesLoading = true;
      })
      .addCase(getClientDetailsThunk.fulfilled, (state, action) => {
        state.privilegesList = action.payload || [];
        state.privilegesLoading = false;
      })
      .addCase(getClientDetailsThunk.rejected, (state) => {
        state.privilegesLoading = false;
      })
      .addCase(editUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(editUserThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(editUserThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(saveUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveUserThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveUserThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getDraftUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDraftUsersThunk.fulfilled, (state, action) => {
        state.draftUsersList = action.payload || [];
        state.loading = false;
      })
      .addCase(getDraftUsersThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(activateUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(activateUserThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(activateUserThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deactivateUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deactivateUserThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deactivateUserThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteUserThunk.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { clearPrivilegesList, setModifiedPrivileges, clearUserDetails } = usersSlice.actions;
export default usersSlice.reducer;
